import type { IndustryPage } from '../industries';

const lines = [
  { item: '16 x 7 ft steel door, 2 in polyurethane insulation, short-panel design, installed', amount: '$2,150' },
  { item: 'Belt-drive opener, two remotes, wall console and keypad', amount: '$520' },
  { item: 'Remove and dispose of the old wood door, tracks and torsion spring', amount: '$150' },
  { item: 'Optional: insulated window row with clear glass on the top section', amount: '$310' },
];

const faq = [
  {
    q: 'What should a garage door installation quote include?',
    a: 'The door model, size, material, insulation and color, the opener and its accessories, removal of the old door, the lead time from the manufacturer, what happens if the opening needs framing or header work, and how the manufacturer warranty differs from your own workmanship terms.',
  },
  {
    q: 'How do I let the homeowner choose between upgrades?',
    a: 'Quote the base door and put upgrades, such as a window row, a quieter opener or a keypad, as optional lines. The homeowner ticks the ones they want on the quote page and sees the total change before signing.',
  },
  {
    q: 'Should I take a deposit before ordering a custom door?',
    a: 'Many installers do, because a door made for one opening is hard to sell to anyone else. Put your deposit rule on the quote so it is part of what the homeowner signs, before the order goes to the manufacturer.',
  },
  {
    q: 'Can Scopie dispatch emergency spring and cable calls?',
    a: 'No. Scopie is built for quoted installations and replacements, not live call dispatch or technician tracking.',
  },
];

const page: IndustryPage = {
  slug: 'garage-door',
  order: 15,
  group: 'Home services',
  path: '/for/garage-door',
  primaryKeyword: 'garage door business software',
  secondaryKeywords: ['garage door estimate template', 'garage door installation quote'],
  title: 'Garage Door Software for Installation Quotes | Scopie', // 52
  metaDescription:
    'Quote garage door replacements with the door, opener and tear-out on separate lines, offer windows and keypads as upgrades, and sign before you order.', // 150
  h1: 'Installation quotes for garage door replacements',
  answerFirst:
    'Scopie gives garage door installers a replacement quote with the door, the opener and removal of the old door on separate lines, and upgrades like a window row or keypad the homeowner can tick. The deposit rule is signed with the quote, so a door built for one opening is never ordered on a handshake.',
  exampleQuote: { label: 'Sample door replacement quote', lines, total: '$3,130', depositNote: 'Order deposit and lead-time wording are the installer\'s own clauses.' },
  blocks: [
    {
      type: 'text',
      id: 'tonight',
      h2: 'Nobody picks a garage door in the driveway',
      lead: '"Let me show my wife the color tonight."',
      paragraphs: [
        'A garage door is the biggest piece of the front of the house, and homeowners want to think about it. A brochure and a number on the back of a card get lost. A quote link they can open after dinner, with the exact model, size and options, keeps you in the conversation while they decide.',
        'Copy the link and text it before you leave. When they are ready, they choose their upgrades, read your terms and sign on the same page.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A two-car replacement with an optional window row',
      label: 'Sample quote: 16 x 7 insulated steel door and opener',
      lines,
      total: '$3,130',
      totalLabel: 'Total with the window row',
      note: 'Example figures. The window row is an optional upgrade the homeowner can tick or leave off.',
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Install quotes that cost the installer',
      style: 'mistakes',
      items: [
        { title: 'Ordering on a verbal yes', text: 'Custom sizes and colors are not returnable. Get the signature and the deposit rule first.' },
        { title: 'Opener bundled into the door price', text: 'Some homeowners keep their opener. Separate lines let them decide.' },
        { title: 'Framing assumed sound', text: 'Rotten jambs or a sagging header change the job. Say they are quoted when found.' },
        { title: 'Lead time not stated', text: 'Manufacturer lead times vary by model and season. Write the expected wait.' },
        { title: 'Warranty blurred', text: 'Explain what the manufacturer covers and what your workmanship terms cover.' },
      ],
    },
    {
      type: 'list',
      id: 'questions',
      h2: 'Questions homeowners ask before signing',
      style: 'bullets',
      items: [
        { text: 'How long until the door arrives?' },
        { text: 'Will you take the old door away?' },
        { text: 'Does the price include the opener, and can I keep mine?' },
        { text: 'How loud is a belt drive compared with a chain?' },
        { text: 'What happens if the frame is rotten?' },
      ],
    },
    {
      type: 'text',
      id: 'install-day',
      h2: 'When the door arrives',
      paragraphs: [
        'Once the door is in your shop, schedule the install with the installers and the truck it needs. Installers see the address and time, not the price. After the opener is programmed, share the invoice link, and the homeowner stays in your client list for the spring replacement or the second bay.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Garage door quote questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/handyman', anchor: 'Scopie for handyman businesses', status: 'live' },
    { href: '/for/concrete', anchor: 'Scopie for concrete contractors', status: 'live' },
    { href: '/templates/garage-door-estimate-template', anchor: 'free garage door estimate template', status: 'planned' },
  ],
  cta: {
    heading: 'Leave the quote behind, not a brochure',
    text: 'Load your door models, openers and upgrades into Scopie and send each replacement quote before you pull out of the driveway.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
